import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Save } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminShell from "@/components/AdminShell";

export default function AdminSEO() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [overrides, setOverrides] = useState([]);
  const [form, setForm] = useState({ path: "/", title: "", description: "", og_image: "", noindex: false });
  const [preview, setPreview] = useState(null);
  const [saved, setSaved] = useState("");

  const load = useCallback(() => { api.get("/seo/overrides").then((r) => setOverrides(r.data)).catch(() => {}); }, []);

  useEffect(() => { if (ready && (user === false || (user && user.role !== "admin"))) navigate("/"); }, [ready, user, navigate]);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  const resolve = async () => {
    const { data } = await api.get("/seo/resolve", { params: { path: form.path } });
    setPreview(data);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { path: form.path, noindex: form.noindex };
    if (form.title) payload.title = form.title;
    if (form.description) payload.description = form.description;
    if (form.og_image) payload.og_image = form.og_image;
    const { data } = await api.post("/seo/overrides", payload);
    setPreview(data.resolved);
    setSaved("Saved. Live SEO for this page is updated.");
    setTimeout(() => setSaved(""), 2500);
    load();
  };

  const input = "w-full rounded-2xl border-2 border-rose/50 bg-cream px-4 py-3 outline-none focus:border-teal transition-colors";

  return (
    <AdminShell active="seo" title="SEO Admin" subtitle="Per-page meta, OG & indexing—rendered by the backend.">
      <form onSubmit={save} className="mt-2 bg-white rounded-3xl border border-rose/30 shadow-card p-6 sm:p-8 space-y-4" data-testid="seo-form">
        <div>
          <label className="block text-sm font-semibold text-plum mb-1.5">Page path</label>
          <div className="flex gap-2">
            <input value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} data-testid="seo-path"
              placeholder="/guides/strength-training-after-50" className={input} />
            <button type="button" onClick={resolve} data-testid="seo-preview-btn"
              className="rounded-2xl border-2 border-rose/50 px-4 font-semibold text-plum hover:border-teal whitespace-nowrap">Preview</button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-plum mb-1.5">Title override</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} data-testid="seo-title" placeholder="Leave blank to use the default" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-plum mb-1.5">Meta description override</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={`${input} resize-none`} data-testid="seo-desc" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-plum mb-1.5">OG image URL</label>
          <input value={form.og_image} onChange={(e) => setForm({ ...form, og_image: e.target.value })} className={input} data-testid="seo-ogimage" />
        </div>
        <label className="flex items-center gap-3 text-plum-ink">
          <input type="checkbox" checked={form.noindex} onChange={(e) => setForm({ ...form, noindex: e.target.checked })} data-testid="seo-noindex" className="h-5 w-5 accent-teal" />
          Set <code className="text-sm">noindex</code> for this page
        </label>
        {saved && <p className="text-sm text-teal" data-testid="seo-saved">{saved}</p>}
        <button type="submit" data-testid="seo-save"
          className="inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3 font-semibold text-white hover:bg-teal-dark transition-colors"><Save className="h-4 w-4" /> Save override</button>
      </form>

      {preview && (
        <div className="mt-6 bg-plum text-cream rounded-3xl p-6" data-testid="seo-preview">
          <p className="text-xs uppercase tracking-wide text-rose">Live resolved SEO</p>
          <p className="mt-2 font-display text-xl">{preview.title}</p>
          <p className="mt-1 text-cream/80 text-sm">{preview.description}</p>
          <p className="mt-2 text-xs text-cream/60">canonical: {preview.canonical} · robots: {preview.noindex ? "noindex" : "index"} · type: {preview.type}</p>
          {preview.json_ld && <pre className="mt-3 text-xs bg-black/20 rounded-xl p-3 overflow-auto">{JSON.stringify(preview.json_ld, null, 2)}</pre>}
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-display text-2xl text-plum mb-3">Existing overrides</h2>
        {overrides.length === 0 ? <p className="text-mutedplum">No overrides yet—pages use smart backend defaults.</p> : (
          <div className="space-y-2">
            {overrides.map((o) => (
              <div key={o.path} className="bg-white rounded-2xl border border-rose/30 p-4 flex items-center justify-between gap-4" data-testid={`override-${o.path}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-plum truncate">{o.path}</p>
                  <p className="text-sm text-mutedplum truncate">{o.title || "(default title)"}</p>
                </div>
                <button onClick={() => setForm({ path: o.path, title: o.title || "", description: o.description || "", og_image: o.og_image || "", noindex: !!o.noindex })}
                  className="text-teal font-semibold text-sm shrink-0">Edit</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
